import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Copy, MessageCircle, User } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { Logo } from "./Logo";
import { NewsletterForm } from "./NewsletterForm";
import { ContactButton } from "./ContactFormDialog";
import { CONTACT, NAV_LINKS, waLink } from "../../data/content";

export const Contact = () => {
  const copyEmail = async () => {
    try {
      await navigator.clipboard?.writeText(CONTACT.email);
      toast.success("Adresse e-mail copiée", {
        description: CONTACT.email,
      });
    } catch {
      toast.info("Écrivez-nous", {
        description: CONTACT.email,
      });
    }
  };

  return (
    <footer
      id="contact"
      data-testid="contact-footer"
      className="relative overflow-hidden bg-pblv-ink text-pblv-bg"
    >
      <div className="pointer-events-none absolute -left-32 top-10 h-80 w-80 rounded-full bg-pblv-mauve/25 blur-[130px]" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-80 w-80 rounded-full bg-pblv-rose/15 blur-[130px]" />

      <div className="relative mx-auto max-w-[1400px] px-5 py-24 md:px-8 md:py-32">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-pblv-rose">
            Contactez-nous
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 max-w-4xl font-serif text-5xl font-medium leading-[0.98] tracking-tight md:text-8xl">
            Offrez-vous
            <br />
            <span className="italic text-pblv-rose">une T'POZ.</span>
          </h2>
        </Reveal>

        <div className="mt-14 flex flex-wrap gap-4">
          <Reveal delay={0.12}>
            <a
              href={waLink("Bonjour, je souhaite des informations sur le réseau T'POZ.")}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-whatsapp-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-[#25D366] px-7 py-4 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-1 hover:brightness-105"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={1.8} />
              Nous contacter sur WhatsApp
            </a>
          </Reveal>
          <Reveal delay={0.15}>
            <ContactButton
              subject="Contact — T'POZ"
              testId="footer-email-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-pblv-bg px-7 py-4 text-sm font-semibold text-pblv-ink transition-all duration-300 hover:-translate-y-1 hover:bg-pblv-rose"
            >
              <Mail className="h-4 w-4" strokeWidth={1.8} />
              Nous écrire
            </ContactButton>
          </Reveal>
          <Reveal delay={0.22}>
            <a
              href={CONTACT.phoneHref}
              data-testid="footer-phone-cta"
              className="group inline-flex items-center gap-3 rounded-full border border-white/20 px-7 py-4 text-sm font-semibold text-pblv-bg transition-all duration-300 hover:-translate-y-1 hover:border-white/50 hover:bg-white/5"
            >
              <Phone className="h-4 w-4" strokeWidth={1.8} />
              {CONTACT.phone}
            </a>
          </Reveal>
          <Reveal delay={0.29}>
            <button
              onClick={copyEmail}
              data-testid="footer-copy-email"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-4 text-sm font-semibold text-pblv-bg/80 transition-all duration-300 hover:-translate-y-1 hover:border-white/50 hover:text-pblv-bg"
              aria-label="Copier l'adresse e-mail"
            >
              <Copy className="h-4 w-4" strokeWidth={1.8} />
              {CONTACT.email}
            </button>
          </Reveal>
        </div>

        <div className="mt-20 grid grid-cols-1 gap-10 border-t border-white/10 pt-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <Logo dark />
            <p className="mt-5 flex items-start gap-3 text-sm leading-relaxed text-pblv-bg/60">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-pblv-sage" strokeWidth={1.8} />
              {CONTACT.address}
            </p>
            <p className="mt-3 text-sm text-pblv-bg/60">{CONTACT.website}</p>
          </div>

          <div className="md:col-span-3 md:col-start-7">
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-pblv-bg/40">
              Explorer
            </p>
            <ul className="flex flex-col gap-3">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    data-testid={`footer-nav-${l.href.replace("#", "")}`}
                    className="text-sm text-pblv-bg/70 transition-colors hover:text-pblv-bg"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3 md:col-start-10">
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-pblv-bg/40">
              Newsletter Répit
            </p>
            <p className="mb-5 text-sm leading-relaxed text-pblv-bg/70">
              Chaque mois, les nouveaux lieux T'POZ et des inspirations bien-être dans votre boîte.
            </p>
            <NewsletterForm />
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-8 text-xs text-pblv-bg/40 md:flex-row md:items-center">
          <p>
            © {new Date().getFullYear()} Association Plus Belle la Vie 971. Tous
            droits réservés.
          </p>
          <div className="flex flex-wrap items-center gap-5">
            <a
              href="/espace"
              data-testid="footer-espace-link"
              className="inline-flex items-center gap-1.5 font-semibold uppercase tracking-[0.15em] text-pblv-bg/60 transition-colors hover:text-pblv-rose"
            >
              <User className="h-3.5 w-3.5" /> Mon espace T'POZ
            </a>
            <a
              href="/presse"
              data-testid="footer-presse-link"
              className="font-semibold uppercase tracking-[0.15em] text-pblv-bg/60 transition-colors hover:text-pblv-rose"
            >
              Espace presse
            </a>
            <p>Découvrir · Se reconnecter · Rayonner</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Contact;
